import React, { useState, useEffect } from 'react';
import { SearchIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';

//
// EndpointCard is unchanged. 
// It's the same component you already wrote, which displays the details, parameters, etc.
//
const EndpointCard = ({
  endpoint,
  method = 'GET',
  description,
  parameters = [],
  responses = [],
  category
}) => {
  const [expanded, setExpanded] = useState(false);
  const [paramValues, setParamValues] = useState(
    parameters.reduce((acc, param) => ({ ...acc, [param.name]: '' }), {})
  );
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleParamChange = (name, value) => {
    setParamValues(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    document.title = 'API Documentation';
  }, []);

  // Execute the API request (either GET or special CSV download).
  const executeApi = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Start with the raw endpoint from props (e.g. "/api/proteins/compartment/:compartment")
      let url = endpoint;

      // 1) Identify path parameters of the form :paramName
      const replacedParams = new Set();

      parameters.forEach(param => {
        // If 'endpoint' includes ":compartment" or ":hdoid" or whatever param.name is
        if (url.includes(`:${param.name}`)) {
          const value = paramValues[param.name].trim();
          if (value) {
            // Replace ":compartment" in the string with e.g. "Presynaptic"
            url = url.replace(`:${param.name}`, encodeURIComponent(value));
            replacedParams.add(param.name);
          }
        }
      });

      // 2) For GET, append leftover params as query string
      if (method.toUpperCase() === 'GET') {
        const queryParams = [];
        for (const param of parameters) {
          const value = paramValues[param.name].trim();
          // If we DIDN'T already replace it in the path, we'll add it as a query param
          if (!replacedParams.has(param.name) && value !== '') {
            queryParams.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(value)}`);
          }
        }
        if (queryParams.length > 0) {
          const joiner = url.includes('?') ? '&' : '?';
          url += `${joiner}${queryParams.join('&')}`;
        }
      }

      // 3) Fetch
      const response = await fetch(url, { method });
      if (category === 'data' || category === 'relationship' || category === 'csv') {
        // It's a CSV download route
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = urlBlob;

        // Attempt to read filename
        const disposition = response.headers.get('Content-Disposition');
        let filename = 'download.csv';
        if (disposition && disposition.includes('filename=')) {
          const match = disposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) filename = match[1];
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setResult(null);
      } else {
        // For JSON endpoints
        const data = await response.json();
        setResult(data);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel = loading
    ? 'Executing...'
    : (category === 'data' || category === 'relationship' || category === 'csv')
      ? 'Download CSV'
      : 'Execute API';

  return (
    <div className="border rounded-md mb-4">
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center">
          <span className="bg-blue-100 text-blue-800 font-mono text-sm px-2 py-1 rounded mr-3">
            {method}
          </span>
          <span className="font-mono text-sm md:text-base">{endpoint}</span>
        </div>
        {expanded ?
          <ChevronDownIcon size={20} className="text-gray-500" /> :
          <ChevronRightIcon size={20} className="text-gray-500" />
        }
      </div>

      {expanded && (
        <div className="p-4 border-t">
          <p className="mb-4 text-gray-700">{description}</p>

          {parameters.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">Parameters</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left font-medium">Name</th>
                      <th className="px-4 py-2 text-left font-medium">Type</th>
                      <th className="px-4 py-2 text-left font-medium">Description</th>
                      <th className="px-4 py-2 text-left font-medium">Required</th>
                      <th className="px-4 py-2 text-left font-medium">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parameters.map((param, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-mono">{param.name}</td>
                        <td className="px-4 py-2">{param.type}</td>
                        <td className="px-4 py-2">{param.description}</td>
                        <td className="px-4 py-2">{param.required ? 'Yes' : 'No'}</td>
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            className="border rounded p-1 text-sm"
                            value={paramValues[param.name]}
                            onChange={(e) => handleParamChange(param.name, e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {responses.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold mb-2">Responses</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                      <th className="px-4 py-2 text-left font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((resp, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 font-mono">{resp.status}</td>
                        <td className="px-4 py-2">{resp.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-4">
            <button
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
              onClick={executeApi}
              disabled={loading}
            >
              {buttonLabel}
            </button>
          </div>

          {result && (
            <div className="mt-4">
              <h4 className="text-sm font-semibold mb-2">Live API Response</h4>
              <pre className="bg-gray-100 p-3 rounded overflow-x-auto text-xs">
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}

          {error && (
            <div className="mt-4 text-red-600">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

//
// Full APIDocMain below,
// including the updated categories and endpoints arrays
//
const APIDocMain = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  // 1) Extend categories to accommodate the new ones.
  const categories = [
    { id: 'all', name: 'All Endpoints' },
    { id: 'search', name: 'Search Endpoints' },
    { id: 'stats', name: 'Statistics Endpoints' },
    { id: 'data', name: 'Data Endpoints (CSV)' },
    { id: 'relationship', name: 'Relationship Endpoints (CSV)' },

    // -- Newly added categories for clarity:
    { id: 'protein', name: 'Protein Endpoints' },
    { id: 'classification', name: 'Disease Classification' },
    { id: 'filter', name: 'Filter Endpoints' },
    { id: 'csv', name: 'Additional CSV Exports' },

    // You might also split them further, e.g. 'disease', 'gene', 'paper',
    // if you want. But for simplicity, we'll rely on the above categories
    // or continue to use 'csv' for newly introduced CSV routes.
  ];

  // 2) This array merges your existing endpoints with the newly added ones.
  //    Each object must have {category, endpoint, method, description, parameters, responses}.
  const endpoints = [
    //
    // ====== ORIGINAL SEARCH ENDPOINTS ======
    //
    {
      category: 'search',
      endpoint: '/api/consensus/genes',
      method: 'GET',
      description: 'Returns a list of consensus gene Entrez IDs from the Human_syn_top5.txt file as a JSON response.',
      parameters: [],
      responses: [
        { status: '200', description: 'Consensus gene Entrez IDs retrieved successfully' },
        { status: '500', description: 'An error occurred retrieving consensus genes' }
      ]
    },
    {
      category: 'search',
      endpoint: '/api/gene/search',
      method: 'GET',
      description: 'Search for genes by name/entrez and return data including papers, diseases, mutations, syngo, etc.',
      parameters: [
        { name: 'q', type: 'string', description: 'Search term for gene name/entrez', required: true }
      ],
      responses: [
        { status: '200', description: 'Search completed successfully' },
        { status: '400', description: 'Search term is required' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'search',
      endpoint: '/api/paper/search',
      method: 'GET',
      description: 'Search for papers by PMID, year, name, or description. Supports pagination. Also returns associated genes, mutations, etc.',
      parameters: [
        { name: 'q', type: 'string', description: 'Paper search term', required: true },
        { name: 'page', type: 'number', description: 'Pagination page', required: false },
        { name: 'pageSize', type: 'number', description: 'Pagination size', required: false },
        // etc. (Add more if you want to show them)
      ],
      responses: [
        { status: '200', description: 'Search completed successfully' },
        { status: '400', description: 'Search term is required' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'search',
      endpoint: '/api/disease/search',
      method: 'GET',
      description: 'Search for diseases by HDOID/description, returning genes, mutations, and papers. Pagination supported.',
      parameters: [
        { name: 'q', type: 'string', description: 'Disease search term', required: true }
      ],
      responses: [
        { status: '200', description: 'Search completed successfully' },
        { status: '400', description: 'Search term is required' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'search',
      endpoint: '/api/autocomplete',
      method: 'GET',
      description: 'Autocomplete suggestions for genes, diseases, or papers. Minimum length of 2 characters recommended.',
      parameters: [
        { name: 'q', type: 'string', description: 'Partial text to autocomplete', required: true },
        { name: 'type', type: 'string', description: 'Optional type filter: "all", "gene", "disease", or "paper".', required: false }
      ],
      responses: [
        { status: '200', description: 'Autocomplete suggestions retrieved' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    //
    // ====== ORIGINAL STATISTICS ENDPOINTS ======
    //
    {
      category: 'stats',
      endpoint: '/api/stats/new-diseases',
      method: 'GET',
      description: 'Returns statistics on the number of new diseases discovered each year (based on knownFrom).',
      parameters: [],
      responses: [
        { status: '200', description: 'New diseases stats retrieved' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'stats',
      endpoint: '/api/stats/accumulated-diseases',
      method: 'GET',
      description: 'Returns accumulated disease stats over time.',
      parameters: [],
      responses: [
        { status: '200', description: 'Accumulated diseases stats retrieved' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'stats',
      endpoint: '/api/stats/accumulated-gene-localizations',
      method: 'GET',
      description: 'Returns stats on gene localizations accumulated over time for consensus genes.',
      parameters: [],
      responses: [
        { status: '200', description: 'Accumulated gene localizations retrieved' },
        { status: '404', description: 'No valid data found' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    //
    // ====== ORIGINAL DATA (CSV) ENDPOINTS ======
    //
    // (These trigger CSV downloads)
    {
      category: 'data',
      endpoint: '/api/brain-regions',
      method: 'GET',
      description: 'Downloads all brain regions data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/diseases',
      method: 'GET',
      description: 'Downloads all diseases data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/genes',
      method: 'GET',
      description: 'Downloads all genes data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/localisations',
      method: 'GET',
      description: 'Downloads all localisations data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/methods',
      method: 'GET',
      description: 'Downloads all methods data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/mutations',
      method: 'GET',
      description: 'Downloads all mutations data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/papers',
      method: 'GET',
      description: 'Downloads all papers data as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/paper-genes',
      method: 'GET',
      description: 'Downloads paper-gene relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/paper-mutations',
      method: 'GET',
      description: 'Downloads paper-mutation relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/specie-regions',
      method: 'GET',
      description: 'Downloads species-region relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'data',
      endpoint: '/api/species',
      method: 'GET',
      description: 'Downloads all species data as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    //
    // ====== ORIGINAL RELATIONSHIP (CSV) ENDPOINTS ======
    //
    {
      category: 'relationship',
      endpoint: '/api/genes-diseases',
      method: 'GET',
      description: 'Genes-to-diseases relationships as a CSV file.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/mutations-details',
      method: 'GET',
      description: 'Mutations with gene/disease associations as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'An error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/papers-genes-regions',
      method: 'GET',
      description: 'Papers-genes-brainRegions relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/species-regions',
      method: 'GET',
      description: 'Species-brainRegions relationships as CSV. (Note duplicate route naming in code).',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/mutations-papers-genes',
      method: 'GET',
      description: 'Mutation-paper-gene relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/papers-methods-localisation',
      method: 'GET',
      description: 'Paper-method-localisation relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/genes-regions-species',
      method: 'GET',
      description: 'Genes-brainRegions-species relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/diseases-mutations-papers',
      method: 'GET',
      description: 'Disease-mutation-paper relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/brain-regions-hierarchy',
      method: 'GET',
      description: 'Hierarchy of brain regions as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/species-genes-diseases',
      method: 'GET',
      description: 'Species -> Genes -> Diseases relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/papers-genes-mutations-overview',
      method: 'GET',
      description: 'Comprehensive paper-gene-mutation overview as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/localisation-genes-regions',
      method: 'GET',
      description: 'Localisation-gene-region relationships as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/full-gene-full-paper-full-region',
      method: 'GET',
      description: 'Genes + Papers + Localisations + Brain regions (comprehensive) as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/full-gene-full-disease',
      method: 'GET',
      description: 'Gene + Disease relationships (full) as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/full-gene-paper',
      method: 'GET',
      description: 'Gene + Paper relationships (full) as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'relationship',
      endpoint: '/api/full-gene-full-paper',
      method: 'GET',
      description: 'Gene + full paper details + localisations as CSV.',
      parameters: [],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '500', description: 'Error occurred' }
      ]
    },

    //
    // ====== NEW: PROTEIN ENDPOINTS ======
    //
    {
      category: 'protein',
      endpoint: '/api/protein/lookup',
      method: 'GET',
      description: 'Uses MyGeneInfo to find gene symbols for a given protein name, then returns which exist in local DB.',
      parameters: [
        { name: 'name', type: 'string', description: 'Protein name to look up', required: true }
      ],
      responses: [
        { status: '200', description: 'Protein lookup succeeded' },
        { status: '400', description: 'Protein name missing' },
        { status: '500', description: 'Internal server error' }
      ]
    },
    {
      category: 'protein',
      endpoint: '/api/proteins/compartments',
      method: 'GET',
      description: 'Summaries how many consensus proteins map to each major synaptic compartment (presyn, postsyn, etc.).',
      parameters: [],
      responses: [
        { status: '200', description: 'Compartment counts retrieved' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'protein',
      endpoint: '/api/proteins/compartment/:compartment',
      method: 'GET',
      description: 'Returns the consensus proteins for a specific compartment, plus paper counts/frequency info.',
      parameters: [
        { name: 'compartment', type: 'string', description: 'Presynaptic, Postsynaptic, etc.', required: true }
      ],
      responses: [
        { status: '200', description: 'Proteins retrieved' },
        { status: '400', description: 'Invalid compartment' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/proteins/compartment/:compartment/csv',
      method: 'GET',
      description: 'Downloads the proteins for a given compartment as CSV with their associated papers.',
      parameters: [
        { name: 'compartment', type: 'string', description: 'Presynaptic, Postsynaptic, etc.', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Invalid compartment' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'protein',
      endpoint: '/api/proteins/brain-regions',
      method: 'GET',
      description: 'Enhanced summary of proteins by brain region & compartment, optionally including hierarchy.',
      parameters: [
        { name: 'top_limit', type: 'number', description: 'How many top proteins to return per region-compartment', required: false }
      ],
      responses: [
        { status: '200', description: 'Protein distribution retrieved' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/proteins/brain-regions/csv',
      method: 'GET',
      description: 'Comprehensive CSV of consensus proteins by brain region, optional filters.',
      parameters: [
        { name: 'region_id', type: 'number', description: 'Brain region ID, or 1 for entire brain', required: false },
        { name: 'compartment', type: 'string', description: 'e.g. Presynaptic, All, etc.', required: false }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Invalid param' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/proteins/brain-regions/:region_id/:localization/csv',
      method: 'GET',
      description: 'Brain region + compartment–specific CSV of consensus proteins (with paper data).',
      parameters: [
        { name: 'region_id', type: 'number', description: 'Brain region ID', required: true },
        { name: 'localization', type: 'string', description: 'Presynaptic, Postsynaptic, etc.', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Invalid region or localization' },
        { status: '500', description: 'Error occurred' }
      ]
    },

    //
    // ====== NEW: DISEASE CLASSIFICATION ENDPOINTS ======
    //
    {
      category: 'classification',
      endpoint: '/api/disease-classifications',
      method: 'GET',
      description: 'Returns ICD-11 chapter summaries (chapter #, disease count, gene count, etc.) from Human_syn_top5 data.',
      parameters: [],
      responses: [
        { status: '200', description: 'Classification data retrieved' },
        { status: '404', description: 'File not found' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'classification',
      endpoint: '/api/disease-classifications/chapter/:chapter',
      method: 'GET',
      description: 'Detailed ICD-11 info for a specific chapter (OMIM references, genes, disease count, etc.).',
      parameters: [
        { name: 'chapter', type: 'string', description: 'Chapter number, e.g. "01" or "02"', required: true }
      ],
      responses: [
        { status: '200', description: 'Chapter data retrieved' },
        { status: '404', description: 'Chapter not found' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'classification',
      endpoint: '/api/disease-classifications/chapter/:chapter/csv',
      method: 'GET',
      description: 'CSV of all diseases + genes for a given ICD-11 chapter.',
      parameters: [
        { name: 'chapter', type: 'string', description: 'Chapter number', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '404', description: 'No diseases found' },
        { status: '500', description: 'Error occurred' }
      ]
    },

    //
    // ====== NEW: FILTER ENDPOINTS (DISEASE/PAPER) ======
    //
    {
      category: 'filter',
      endpoint: '/api/disease/mutations/filter',
      method: 'GET',
      description: 'Filter/paginate mutations for a given disease, marking consensus genes if relevant.',
      parameters: [
        { name: 'hdoid', type: 'string', description: 'Disease ID', required: true },
        { name: 'page', type: 'number', description: 'Page', required: false },
        { name: 'pageSize', type: 'number', description: 'Page size', required: false }
      ],
      responses: [
        { status: '200', description: 'Mutations filtered' },
        { status: '400', description: 'hdoid missing' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'filter',
      endpoint: '/api/disease/papers/filter',
      method: 'GET',
      description: 'Filter/paginate papers associated with a disease, marking consensus genes, etc.',
      parameters: [
        { name: 'hdoid', type: 'string', description: 'Disease ID', required: true },
        { name: 'page', type: 'number', description: 'Page', required: false },
        { name: 'pageSize', type: 'number', description: 'Page size', required: false }
      ],
      responses: [
        { status: '200', description: 'Papers filtered' },
        { status: '400', description: 'hdoid missing' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'filter',
      endpoint: '/api/disease/genes/filter',
      method: 'GET',
      description: 'Filter/paginate genes for a disease, handle consensus vs. non-consensus, optional text filter, etc.',
      parameters: [
        { name: 'hdoid', type: 'string', required: true },
        { name: 'term', type: 'string', required: false },
        { name: 'mode', type: 'string', description: 'all, consensus, or non-consensus', required: false },
        { name: 'page', type: 'number', required: false },
        { name: 'pageSize', type: 'number', required: false }
      ],
      responses: [
        { status: '200', description: 'Genes filtered' },
        { status: '400', description: 'hdoid missing' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'filter',
      endpoint: '/api/paper/genes/filter',
      method: 'GET',
      description: 'Filter/paginate genes for a paper, can specify display mode (consensus vs. non) and localization, etc.',
      parameters: [
        { name: 'pmid', type: 'string', required: true },
        { name: 'term', type: 'string', required: false },
        { name: 'mode', type: 'string', description: 'all, consensus, or non-consensus', required: false },
        { name: 'localization', type: 'string', required: false },
        { name: 'page', type: 'number', required: false },
        { name: 'pageSize', type: 'number', required: false }
      ],
      responses: [
        { status: '200', description: 'Genes filtered' },
        { status: '400', description: 'pmid missing' },
        { status: '500', description: 'Error occurred' }
      ]
    },

    //
    // ====== NEW: PER-GENE/PER-DISEASE/PER-PAPER CSV + DETAILS ======
    // Put them under 'csv' category or keep them separate – here we use 'csv' for clarity.
    //
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/csv',
      method: 'GET',
      description: 'Full details for a specific gene as CSV (OMIM entries, papers, diseases, mutations...).',
      parameters: [
        { name: 'gene_id', type: 'string', description: 'Gene ID or name or HumanEntrez', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '404', description: 'Gene not found' },
        { status: '500', description: 'Error occurred' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/allpapers/csv',
      method: 'GET',
      description: 'All papers for a specific gene in CSV form.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/alldiseases/csv',
      method: 'GET',
      description: 'All diseases for a gene in CSV form.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/allmutations/csv',
      method: 'GET',
      description: 'All mutations for a gene in CSV form.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/allomimentries/csv',
      method: 'GET',
      description: 'All OMIM entries for a gene in CSV form.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/allsyngo/csv',
      method: 'GET',
      description: 'SynGO data for a gene (by HumanEntrez) as CSV.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/:gene_id/allsyngoportal/csv',
      method: 'GET',
      description: 'SynGO data for a gene (by HumanEntrez) as CSV.',
      parameters: [
        { name: 'gene_id', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/gene/search/csv',
      method: 'GET',
      description: 'Gene-search results in CSV format, with summary stats (papers, diseases, mutations, etc.).',
      parameters: [
        { name: 'q', type: 'string', description: 'Search term for genes', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Search term missing' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/disease/:hdoid/csv',
      method: 'GET',
      description: 'Full details of a specific disease in CSV format (genes, consensus info, mutations, etc.).',
      parameters: [
        { name: 'hdoid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '404', description: 'Disease not found' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/disease/:hdoid/allgenes/csv',
      method: 'GET',
      description: 'All genes for a disease (CSV), marking consensus genes.',
      parameters: [
        { name: 'hdoid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/disease/:hdoid/allmutations/csv',
      method: 'GET',
      description: 'All mutations for a disease (CSV), marking consensus genes.',
      parameters: [
        { name: 'hdoid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/disease/:hdoid/allpapers/csv',
      method: 'GET',
      description: 'All papers for a disease (CSV), highlighting consensus genes.',
      parameters: [
        { name: 'hdoid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/disease/search/csv',
      method: 'GET',
      description: 'Disease-search results in CSV, includes gene counts, consensus stats, etc.',
      parameters: [
        { name: 'q', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Search term missing' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/paper/:pmid/csv',
      method: 'GET',
      description: 'Full details for a specific paper in CSV form (genes, consensus info, mutations...).',
      parameters: [
        { name: 'pmid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '404', description: 'Paper not found' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/paper/:pmid/allgenes/csv',
      method: 'GET',
      description: 'All genes for a paper as CSV, marking consensus genes.',
      parameters: [
        { name: 'pmid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/paper/:pmid/allmutations/csv',
      method: 'GET',
      description: 'All mutations for a paper as CSV, marking consensus genes.',
      parameters: [
        { name: 'pmid', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' }
      ]
    },
    {
      category: 'csv',
      endpoint: '/api/paper/search/csv',
      method: 'GET',
      description: 'Paper-search results in CSV, including gene counts, consensus stats, etc.',
      parameters: [
        { name: 'q', type: 'string', required: true }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded' },
        { status: '400', description: 'Search term missing' }
      ]
    },
    // Add these to your 'endpoints' array in the APIDocMain component

    // Gene Tab Data
    {
      category: 'search',
      endpoint: '/api/gene/:gene_id/tab/:tab_type',
      method: 'GET',
      description: 'Returns specific tab data for a gene (papers, diseases, mutations, omim, syngo, syngoportal).',
      parameters: [
        { name: 'gene_id', type: 'string', description: 'Gene ID', required: true },
        { name: 'tab_type', type: 'string', description: 'Tab type (papers, diseases, mutations, omim, syngo, syngoportal)', required: true }
      ],
      responses: [
        { status: '200', description: 'Tab data retrieved successfully' },
        { status: '404', description: 'Gene not found' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Gene Papers JSON
    {
      category: 'search',
      endpoint: '/api/gene/:gene_id/papers',
      method: 'GET',
      description: 'Returns all papers associated with a specific gene in JSON format.',
      parameters: [
        { name: 'gene_id', type: 'string', description: 'Gene ID, name, or Entrez ID', required: true }
      ],
      responses: [
        { status: '200', description: 'Papers retrieved successfully' },
        { status: '404', description: 'Gene not found' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Paper Tab Data
    {
      category: 'search',
      endpoint: '/api/paper/tab/:pmid/:tab_type',
      method: 'GET',
      description: 'Returns specific tab data for a paper (genes, mutations, stats).',
      parameters: [
        { name: 'pmid', type: 'string', description: 'Paper PMID', required: true },
        { name: 'tab_type', type: 'string', description: 'Tab type (genes, mutations, stats)', required: true },
        { name: 'term', type: 'string', description: 'Filter term for genes/mutations', required: false },
        { name: 'mode', type: 'string', description: 'Display mode (all, consensus, non-consensus)', required: false },
        { name: 'localization', type: 'string', description: 'Filter by localization (for genes tab)', required: false },
        { name: 'page', type: 'number', description: 'Page number for pagination', required: false },
        { name: 'pageSize', type: 'number', description: 'Items per page', required: false }
      ],
      responses: [
        { status: '200', description: 'Tab data retrieved successfully' },
        { status: '404', description: 'Paper not found' },
        { status: '400', description: 'Invalid tab type' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Paper Mutations Filter
    {
      category: 'filter',
      endpoint: '/api/paper/mutations/filter',
      method: 'GET',
      description: 'Filter/paginate mutations for a paper, with consensus gene marking.',
      parameters: [
        { name: 'pmid', type: 'string', description: 'Paper PMID', required: true },
        { name: 'term', type: 'string', description: 'Filter term', required: false },
        { name: 'mode', type: 'string', description: 'Display mode (all or consensus)', required: false },
        { name: 'page', type: 'number', description: 'Page number', required: false },
        { name: 'pageSize', type: 'number', description: 'Items per page', required: false }
      ],
      responses: [
        { status: '200', description: 'Mutations filtered successfully' },
        { status: '400', description: 'PMID is required' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Gene Table
    {
      category: 'search',
      endpoint: '/api/gene/table',
      method: 'GET',
      description: 'Returns paginated gene table data with options for sorting, filtering, and consensus filtering.',
      parameters: [
        { name: 'page', type: 'number', description: 'Page number', required: false },
        { name: 'pageSize', type: 'number', description: 'Items per page', required: false },
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by (ID, MGI, HumanEntrez, etc.)', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'Gene table data retrieved successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Disease Table
    {
      category: 'search',
      endpoint: '/api/disease/table',
      method: 'GET',
      description: 'Returns paginated disease table data with filtering options.',
      parameters: [
        { name: 'page', type: 'number', description: 'Page number', required: false },
        { name: 'pageSize', type: 'number', description: 'Items per page', required: false },
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by (HDOID, Description)', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to diseases with consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'Disease table data retrieved successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Paper Table
    {
      category: 'search',
      endpoint: '/api/paper/table',
      method: 'GET',
      description: 'Returns paginated paper table data with options for sorting and filtering.',
      parameters: [
        { name: 'page', type: 'number', description: 'Page number', required: false },
        { name: 'pageSize', type: 'number', description: 'Items per page', required: false },
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by (PMID, Year, Name, etc.)', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to papers with consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'Paper table data retrieved successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Gene CSV Download
    {
      category: 'csv',
      endpoint: '/api/gene/csv',
      method: 'GET',
      description: 'Generates a CSV file with gene data based on filter parameters.',
      parameters: [
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Disease CSV Download
    {
      category: 'csv',
      endpoint: '/api/disease/csv',
      method: 'GET',
      description: 'Generates a CSV file with disease data based on filter parameters.',
      parameters: [
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to diseases with consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    },

    // Paper CSV Download
    {
      category: 'csv',
      endpoint: '/api/paper/csv',
      method: 'GET',
      description: 'Generates a CSV file with paper data based on filter parameters.',
      parameters: [
        { name: 'search', type: 'string', description: 'Search term for filtering', required: false },
        { name: 'sortBy', type: 'string', description: 'Column to sort by', required: false },
        { name: 'sortDir', type: 'string', description: 'Sort direction (asc or desc)', required: false },
        { name: 'consensusOnly', type: 'boolean', description: 'Filter to papers with consensus genes only', required: false }
      ],
      responses: [
        { status: '200', description: 'CSV downloaded successfully' },
        { status: '500', description: 'An error occurred' }
      ]
    }
  ]; // end of endpoints array

  // Filter logic
  const filteredEndpoints = endpoints.filter(ep => {
    const matchesSearch =
      searchTerm === '' ||
      ep.endpoint.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ep.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || ep.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold mb-6">API Documentation</h1>

        <div className="mb-8">
          <p className="text-gray-700 mb-4">
            This documentation covers all available endpoints for the SynaptopathyDB, including search, stats,
            data retrieval (CSV), relationship data, protein compartments, classification by ICD-11, and more.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Base URL</h3>
            <code className="bg-white px-2 py-1 rounded text-sm font-mono">SynaptopathyDB.org/api</code>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Response Format</h3>
            <p className="mb-2">
              Most endpoints return JSON. CSV endpoints automatically trigger a CSV file download. Some endpoints
              can be conditionally JSON or CSV, depending on the <code>category</code>.
            </p>
            <p>
              JSON responses generally follow a structure containing <code>success</code>, <code>message</code>, and <code>data</code> fields.
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6 mb-8">
          <div className="w-full md:w-1/4">
            <div className="sticky top-4">
              <div className="mb-6">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search Endpoints
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SearchIcon size={16} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="search"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Filter endpoints..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Categories</h3>
                <nav className="space-y-1">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium ${activeCategory === cat.id
                        ? 'bg-blue-100 text-blue-800'
                        : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      onClick={() => setActiveCategory(cat.id)}
                    >
                      {cat.name}
                      <span className="ml-2 text-xs text-gray-500">
                        {endpoints.filter(e => cat.id === 'all' || e.category === cat.id).length}
                      </span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>

          <div className="w-full md:w-3/4">
            {filteredEndpoints.length > 0 ? (
              <>
                <h2 className="text-xl font-semibold mb-4">
                  {activeCategory === 'all'
                    ? 'All Endpoints'
                    : categories.find(c => c.id === activeCategory)?.name
                  }
                </h2>

                <p className="text-sm text-gray-500 mb-6">
                  Showing {filteredEndpoints.length} out of {endpoints.length} endpoints
                  {searchTerm && ` matching "${searchTerm}"`}
                </p>

                <div>
                  {filteredEndpoints.map((ep) => (
                    <EndpointCard
                      key={`${activeCategory}-${searchTerm}-${ep.endpoint}`}
                      endpoint={ep.endpoint}
                      method={ep.method}
                      description={ep.description}
                      parameters={ep.parameters}
                      responses={ep.responses}
                      category={ep.category}
                    />
                  ))}

                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No endpoints found matching your criteria.</p>
                <button
                  className="mt-4 text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveCategory('all');
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIDocMain;
